import { Link, useNavigate } from "@tanstack/solid-router";
import { For, Show, createMemo, createSignal } from "solid-js";

import { useCreateDeal, useDeals, usePipelines, useStages } from "~/lib/hooks";
import type { AttachableEntity, DealStatus } from "~/lib/types";

type Props = {
  wsKey: () => string;
  entityType: Extract<AttachableEntity, "account" | "contact">;
  entityId: () => string;
};

const STATUS_BADGE: Record<DealStatus, string> = {
  open: "badge-outline",
  won: "badge-success",
  lost: "badge-error",
  abandoned: "badge-ghost",
};

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ""}`.trim();
  }
}

export function DealsForEntityCard(props: Props) {
  const navigate = useNavigate();
  const pipelines = usePipelines(props.wsKey);
  const defaultPipelineId = createMemo(
    () => pipelines.data?.find((p) => p.is_default)?.id ?? pipelines.data?.[0]?.id,
  );
  const stages = useStages(props.wsKey, defaultPipelineId);

  const deals = useDeals(props.wsKey, () => ({
    account_id: props.entityType === "account" ? props.entityId() : undefined,
    contact_id: props.entityType === "contact" ? props.entityId() : undefined,
    limit: 50,
  }));

  const stageNameById = createMemo(() =>
    Object.fromEntries((stages.data ?? []).map((s) => [s.id, s.name])),
  );

  const createMutation = useCreateDeal(props.wsKey);
  const [showCreate, setShowCreate] = createSignal(false);
  const [createError, setCreateError] = createSignal<string | null>(null);

  const handleCreate = async (e: SubmitEvent) => {
    e.preventDefault();
    setCreateError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    if (!name) return;
    const amountStr = String(data.get("amount") ?? "").trim();
    try {
      const deal = await createMutation.mutateAsync({
        name,
        amount: amountStr ? Number(amountStr) : null,
        currency: String(data.get("currency") ?? "EUR") || null,
        account_id: props.entityType === "account" ? props.entityId() : null,
        primary_contact_id: props.entityType === "contact" ? props.entityId() : null,
        expected_close_date: String(data.get("expected_close_date") ?? "") || null,
      });
      setShowCreate(false);
      void navigate({
        to: "/workspaces/$wsKey/deals/$dealId",
        params: { wsKey: props.wsKey(), dealId: deal.id },
      });
    } catch (err) {
      setCreateError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm mt-4">
      <div class="card-body">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold">Deals</h3>
          <button class="btn btn-sm btn-ghost" onClick={() => setShowCreate(true)}>
            + Deal
          </button>
        </div>

        <Show when={deals.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-6 opacity-60 text-sm">
            Loading…
          </div>
        </Show>

        <Show when={deals.data}>
          {(data) => (
            <Show
              when={data().items.length > 0}
              fallback={
                <div class="text-center py-6 opacity-60 text-sm">
                  No deals linked to this {props.entityType} yet.
                </div>
              }
            >
              <ul class="flex flex-col">
                <For each={data().items}>
                  {(d) => (
                    <li class="border-b border-base-200 last:border-b-0 py-2">
                      <div class="flex items-center justify-between gap-3">
                        <Link
                          to="/workspaces/$wsKey/deals/$dealId"
                          params={{ wsKey: props.wsKey(), dealId: d.id }}
                          class="link link-primary font-medium truncate"
                        >
                          {d.name}
                        </Link>
                        <span class="text-sm font-medium shrink-0">
                          {formatMoney(d.amount, d.currency)}
                        </span>
                      </div>
                      <div class="flex items-center gap-2 mt-1 text-xs opacity-70">
                        <span class={`badge badge-xs ${STATUS_BADGE[d.status]}`}>{d.status}</span>
                        <span>{stageNameById()[d.stage_id] ?? d.stage_id.slice(0, 10)}</span>
                        <Show when={d.expected_close_date}>
                          <span>
                            · close {new Date(d.expected_close_date!).toLocaleDateString()}
                          </span>
                        </Show>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          )}
        </Show>
      </div>

      <Show when={showCreate()}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="new-deal-for-entity-title"
        >
          <div class="modal-box">
            <h3 id="new-deal-for-entity-title" class="font-bold text-lg mb-4">
              New deal
            </h3>
            <form onSubmit={handleCreate} class="flex flex-col gap-3">
              <input
                name="name"
                class="input input-bordered"
                placeholder="Deal name *"
                aria-label="Deal name"
                required
              />
              <div class="grid grid-cols-2 gap-3">
                <input
                  name="amount"
                  type="number"
                  step="0.01"
                  class="input input-bordered"
                  placeholder="Amount"
                  aria-label="Amount"
                />
                <input
                  name="currency"
                  class="input input-bordered"
                  placeholder="Currency"
                  aria-label="Currency"
                  maxLength={3}
                  value="EUR"
                />
              </div>
              <label class="label">
                <span class="label-text">Expected close date</span>
              </label>
              <input
                name="expected_close_date"
                type="date"
                class="input input-bordered"
                aria-label="Expected close date"
              />
              <Show when={createError()}>
                <div role="alert" class="alert alert-error text-sm">
                  {createError()}
                </div>
              </Show>
              <div class="modal-action">
                <button type="button" class="btn btn-ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </Show>
    </div>
  );
}
